import {
  Alert,
  Button,
  Loader,
  Modal,
  SelectionState,
  styled,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import z from "zod";
import { useForm, zodResolver } from "@mantine/form";
import { enqueueSnackbar } from "notistack";
import { GetEvidenceQuery } from "../evidence.api.types";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import {
  ChainOfCustodyAPI,
  ChainOfCustodyForm,
  ChainOfCustodyFormSchema,
  ChainOfCustodyFormValues,
  CocQuery,
} from "@/Features";

type BulkCocEvidenceModalProps = {
  open: boolean;
  handleClose?: () => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  selectionState: SelectionState & { query: GetEvidenceQuery };
  defaultFormData?: DefaultBulkCocFormValues;
  type: ChainOfCustodyFormValues["type"];
};

const BulkIntakeEvidenceFormSchema = z.object({
  coc: ChainOfCustodyFormSchema,
  selectionState: z.object({
    selectionStatus: z.enum(["all", "someIncluded", "someExcluded"]),
    selectedItems: z.array(z.union([z.string(), z.number()])),
    excludedItems: z.array(z.union([z.string(), z.number()])),
    query: z.record(z.string(), z.any()), // Set query to object with any key value pairs
    totalSelected: z.number().min(1),
  }),
  confirmTotal: z
    .string({ message: "Confirm Total is required" })
    .min(1, { message: "Confirm Total is required" }),
});

export type BulkCocFormValues = {
  selectionState: {
    selectionStatus: SelectionState["selectionStatus"];
    selectedItems: SelectionState["selectedRowKeys"];
    excludedItems: SelectionState["excludedRowKeys"];
    query: GetEvidenceQuery;
    totalSelected: SelectionState["totalSelected"];
  };
  confirmTotal?: number;
  coc: ChainOfCustodyFormValues;
};

export type DefaultBulkCocFormValues = {
  selectionState?: Partial<BulkCocFormValues["selectionState"]>;
  coc?: Partial<BulkCocFormValues["coc"]>;
};

const SelectedItemsMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: auto;
  width: 100%;

  .thick {
    font-weight: 600;
  }

  .subtle {
    color: ${(props) => props.theme.palette.text.secondary};
  }

  .item-total {
    font-size: 32px;
    font-weight: 600;
    color: ${(props) => props.theme.palette.primary.main};
  }
`;

const ModalSubtitle: React.FC<{
  type: ChainOfCustodyFormValues["type"];
  children?: React.ReactNode;
}> = ({ type, children }) => {
  let message = `Record Chain of Custody ${type} for multiple evidence items at once.`;

  switch (type) {
    case "Intake":
      message = `A chain of custody "Intake" record will be created for each evidence item.`;
      break;
    case "Move":
      message = `A chain of custody "Move" record will be created for each evidence item and moved from their previous location to the new selected location.`;
      break;
    case "Checkout":
      message = `A chain of custody "Checkout" record will be created for each evidence item and moved from their previous location and to your custody (location).`;
      break;
    case "Release":
      message = `A chain of custody "Release" record will be created for each evidence item and marked as "Released".`;
      break;
    case "Destroy":
      message = `A chain of custody "Destroy" record will be created for each evidence item and marked as "Destroyed".`;
      break;
    default:
      break;
  }

  return (
    <Alert variant="info">
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <div>{children}</div>
        <div style={{ fontStyle: "italic", opacity: 0.75 }}>{message}</div>
      </div>
    </Alert>
  );
};

const ExclusionWarning: React.FC<{
  type: ChainOfCustodyFormValues["type"];
}> = ({ type }) => {
  let message = "";

  switch (type) {
    case "Intake":
      message =
        "Any items that already have an intake record, have been released, or have been destroyed are being excluded from your selection.";
      break;
    case "Move":
    case "Checkout":
    case "Release":
    case "Destroy":
      message =
        "Currently released or destroyed items are being excluded from your selection.";
      break;
    default:
      return null;
  }

  return (
    <Alert variant="warning">
      <div style={{ paddingTop: "5px", paddingLeft: "5px" }}>{message}</div>
    </Alert>
  );
};

export const BulkCocEvidenceModal: React.FC<BulkCocEvidenceModalProps> = ({
  open,
  handleClose,
  onSubmit,
  onCancel,
  selectionState,
  defaultFormData,
  type,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<BulkCocFormValues>({
    initialValues: {
      selectionState: {
        selectionStatus: selectionState.selectionStatus,
        selectedItems: selectionState.selectedRowKeys,
        excludedItems: selectionState.excludedRowKeys,
        query: selectionState.query,
        totalSelected: selectionState.totalSelected,
      },
      ...defaultFormData,
    } as BulkCocFormValues,
    validate: zodResolver(BulkIntakeEvidenceFormSchema),
  });

  const { data: bulkUpdateTotal, isLoading: isLoadingBulkUpdateTotal } =
    CocQuery.useGetBulkUpdateTotal(
      {
        coc_type: form.getValues().coc.type,
        selectionState: form.getValues().selectionState,
      },
      { enabled: open }
    );

  const formDisabled = submitting || isLoadingBulkUpdateTotal;
  const totalEvidenceCount = bulkUpdateTotal?.data?.total_evidence_count || 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    const formData = form.getValues();

    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      enqueueSnackbar(
        `Please complete all required fields. ${JSON.stringify(
          Object.values(validationResult.errors)
        )}`,
        {
          variant: "error",
        }
      );
      setSubmitting(false);
      return;
    }
    if (formData.confirmTotal?.toString() !== totalEvidenceCount.toString()) {
      form.setErrors({
        confirmTotal: "Total is not correct",
      });
      setSubmitting(false);
      return;
    }

    await ChainOfCustodyAPI.createBulkCocRecords({
      coc: formData.coc,
      confirmTotal: formData.confirmTotal,
      selectionState: formData.selectionState,
    });

    await queryClient.invalidateQueries({ queryKey: ["evidence:cocData"] });

    onSubmit?.();
    setSubmitting(false);
    handleClose?.();
  };

  const handleCancel = () => {
    form.reset();
    handleClose?.();
  };

  return (
    <Modal open={open} onClose={handleClose} style={{ width: "650px" }}>
      <div style={{ marginBottom: "20px" }}>
        <Modal.Title style={{ marginBottom: "10px" }}>
          Bulk Evidence {type}
        </Modal.Title>
        <ModalSubtitle type={type}>
          <SelectedItemsMessage>
            <div className="item-total">
              {isLoadingBulkUpdateTotal ? <Loader /> : totalEvidenceCount}
            </div>
            {!isLoadingBulkUpdateTotal && (
              <div>
                <strong>{totalEvidenceCount} evidence items</strong> out of the{" "}
                {selectionState.totalSelected} selected items will be updated
                with this action
              </div>
            )}
            {isLoadingBulkUpdateTotal && (
              <div>Calculating total evidence items to be updated...</div>
            )}
            {(bulkUpdateTotal?.data?.child_evidence_count || 0) > 0 && (
              <div
                style={{ fontStyle: "italic", opacity: 0.75 }}
              >{`${bulkUpdateTotal?.data?.child_evidence_count} child items have been added to this action.`}</div>
            )}
          </SelectedItemsMessage>
        </ModalSubtitle>
        <div style={{ marginTop: "10px" }}>
          <ExclusionWarning type={type} />
        </div>
      </div>
      <ChainOfCustodyForm
        type={type}
        disabled={formDisabled}
        showButtonMenu={false}
        defaultFormData={defaultFormData?.coc as ChainOfCustodyFormValues}
        {...form.getInputProps("coc")}
        key={form.key("coc")}
        onCancel={handleClose}
      />
      <div style={{ marginBottom: "10px", marginTop: "10px" }}>
        <TextInput
          placeholder={`Confirm Total...`}
          label={`Enter Total (${totalEvidenceCount}) to Confirm Bulk Evidence Action`}
          description="Enter the total number of evidence items to be updated to confirm this action"
          required={true}
          disabled={formDisabled}
          style={{ width: "100%" }}
          {...form.getInputProps("confirmTotal")}
          key={form.key("confirmTotal")}
        />
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Submit
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
